import { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import ArrowForward from '@mui/icons-material/ArrowForward';
import type { SessionUser } from '@zilian/contracts';
import { BrandLogo } from '@zilian/ui';
import { Wordmark } from '@zilian/ui';
import { api } from '../api';
import { ProductStage } from './Login';

interface Activation {
  workspace: string;
  name: string;
  /** The sign-in account: a phone number (masked) or a member's account name. */
  phone: string;
  accountKind: '手机号' | '账号';
  /** 管理员 or 成员: a member's link comes from the enterprise administrator. */
  who: string;
  first: boolean;
  trialDays: number;
}

/**
 * Where an enterprise administrator lands from the platform's link, or a member from the link
 * their administrator sent after a forgotten password: they set their own password and are
 * signed in. The first time this also starts the trial. Same layout as the login page.
 */
export function Activate({
  token,
  onLogin,
}: {
  token: string;
  onLogin: (user: SessionUser) => void;
}) {
  const [info, setInfo] = useState<Activation | null>(null);
  const [invalid, setInvalid] = useState('');
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api<Activation>(`/auth/activation/${encodeURIComponent(token)}`)
      .then(setInfo)
      .catch((e) => setInvalid(e.message));
  }, [token]);
  const mismatch = !!again && again !== password;
  return (
    <div className="login-page">
      <section className="login-side">
        <div className="login-brand">
          <BrandLogo size={36} />
          <Wordmark height={24} />
        </div>
        <div className="login-main">
          {invalid ? (
            <>
              <h1 className="login-headline">链接已失效</h1>
              <Alert severity="warning" sx={{ mt: 4, maxWidth: 380 }}>
                {invalid}
              </Alert>
            </>
          ) : !info ? (
            <CircularProgress />
          ) : (
            <>
              <h1 className="login-headline">{info.first ? '激活企业账号' : '设置新密码'}</h1>
              <p className="login-lede">
                {info.workspace} · {info.who} {info.name}
                <br />
                登录{info.accountKind}为 {info.phone}
                {info.first ? `，激活后免费试用 ${info.trialDays} 天。` : '。'}
              </p>
              <Box
                component="form"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (password.length < 12) return setError('密码至少 12 位');
                  if (password !== again) return setError('两次输入的密码不一致');
                  setBusy(true);
                  setError('');
                  try {
                    onLogin(await api('/auth/activate', { token, password }));
                  } catch (err) {
                    setError((err as Error).message);
                  } finally {
                    setBusy(false);
                  }
                }}
                sx={{ mt: 5, maxWidth: 380 }}
              >
                {error && (
                  <Alert severity="error" sx={{ mb: 2.5 }}>
                    {error}
                  </Alert>
                )}
                <Stack spacing={2}>
                  <TextField
                    required
                    autoFocus
                    type="password"
                    label="设置登录密码"
                    helperText="至少 12 位"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fff' } }}
                  />
                  <TextField
                    required
                    type="password"
                    label="再次输入"
                    autoComplete="new-password"
                    value={again}
                    error={mismatch}
                    helperText={mismatch ? '两次输入的密码不一致' : ' '}
                    onChange={(e) => setAgain(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fff' } }}
                  />
                  <Button
                    type="submit"
                    size="large"
                    variant="contained"
                    disabled={busy}
                    endIcon={<ArrowForward className="login-arrow" />}
                    sx={{ py: 1.3, fontSize: 15 }}
                  >
                    {busy ? '正在提交…' : info.first ? '激活并登录' : '保存并登录'}
                  </Button>
                </Stack>
                <Typography color="text.secondary" sx={{ fontSize: 12.5, mt: 2.5 }}>
                  之后在小程序里用同一账号首次登录，即可绑定微信。
                </Typography>
              </Box>
            </>
          )}
        </div>
        <div className="login-footnote">企业数据各自独立，仅与合作方共享已授权的交易信息。</div>
      </section>
      <section className="login-showcase">
        <ProductStage />
      </section>
    </div>
  );
}
